/**
 * Unified Color Palette System - Utility Functions
 *
 * パレットシステムとD3の統合を提供するユーティリティ
 */

import * as d3 from 'd3';
import {
  Palette,
  CategoricalPalette,
  SequentialPalette,
  DivergingPalette,
  PaletteCollection,
  isCategoricalPalette,
  isSequentialPalette,
  isDivergingPalette,
} from './palette-types';
import palettesData from './palettes.json';
import logger from '../utils/logger';

/**
 * パレットコレクション（型付き）
 */
const PALETTES: PaletteCollection = palettesData as PaletteCollection;

/**
 * 全てのパレットをフラット配列として取得
 */
export function getAllPalettes(): Palette[] {
  return [
    ...PALETTES.categorical,
    ...PALETTES.sequential,
    ...PALETTES.diverging,
  ];
}

/**
 * カテゴリカルパレット一覧を取得
 */
export function getCategoricalPalettes(): CategoricalPalette[] {
  return PALETTES.categorical;
}

/**
 * シーケンシャルパレット一覧を取得
 */
export function getSequentialPalettes(): SequentialPalette[] {
  return PALETTES.sequential;
}

/**
 * ダイバージングパレット一覧を取得
 */
export function getDivergingPalettes(): DivergingPalette[] {
  return PALETTES.diverging;
}

/**
 * パレットIDからパレットを取得
 *
 * @param paletteId パレットID
 * @returns パレット（見つからない場合はundefined）
 */
export function getPaletteById(paletteId: string): Palette | undefined {
  return getAllPalettes().find((p) => p.id === paletteId);
}

/**
 * カテゴリカルスケールを作成
 *
 * @param paletteId パレットID
 * @param domain ドメイン（カテゴリの配列）
 * @returns D3のカテゴリカルスケール
 *
 * @example
 * ```typescript
 * const scale = createCategoricalScale('midas-default', ['A', 'B', 'C']);
 * console.log(scale('A')); // '#3B82F6'
 * console.log(scale('B')); // '#10B981'
 * ```
 */
export function createCategoricalScale(
  paletteId: string,
  domain?: string[] | number[]
): d3.ScaleOrdinal<string | number, string, never> {
  const palette = getPaletteById(paletteId);

  logger.assert(
    palette != null,
    `[PaletteUtils] Palette not found: ${paletteId}`
  );
  logger.assert(
    isCategoricalPalette(palette!),
    `[PaletteUtils] Palette is not categorical: ${paletteId} (type: ${palette!.type})`
  );

  const scale = d3.scaleOrdinal<string | number, string>().range(palette!.colors);

  if (domain) {
    scale.domain(domain);
  }

  return scale as d3.ScaleOrdinal<string | number, string, never>;
}

/**
 * シーケンシャルスケールを作成
 *
 * @param paletteId パレットID
 * @param domain ドメイン（[min, max]の配列、オプション）
 * @returns D3のシーケンシャルスケール
 *
 * @example
 * ```typescript
 * const scale = createSequentialScale('viridis', [0, 100]);
 * console.log(scale(0));   // '#440154' (最小値の色)
 * console.log(scale(50));  // 中間の色
 * console.log(scale(100)); // '#fde724' (最大値の色)
 * ```
 */
export function createSequentialScale(
  paletteId: string,
  domain?: [number, number]
): d3.ScaleSequential<string, never> {
  const palette = getPaletteById(paletteId);

  logger.assert(
    palette != null,
    `[PaletteUtils] Palette not found: ${paletteId}`
  );
  logger.assert(
    isSequentialPalette(palette!),
    `[PaletteUtils] Palette is not sequential: ${paletteId} (type: ${palette!.type})`
  );

  // D3の補間関数を作成
  // d3.piecewiseを使って色配列から補間関数を生成
  const interpolator = d3.piecewise(d3.interpolateRgb.gamma(2.2), palette!.colors);
  const scale = d3.scaleSequential(interpolator);

  if (domain) {
    scale.domain(domain);
  }

  return scale as d3.ScaleSequential<string, never>;
}

/**
 * ダイバージングスケールを作成
 *
 * @param paletteId パレットID
 * @param domain ドメイン（[min, max]の配列、オプション）
 * @returns D3のダイバージングスケール
 *
 * @example
 * ```typescript
 * const scale = createDivergingScale('rdbu', [-100, 100]);
 * console.log(scale(-100)); // '#67001f' (負の最大値の色、赤)
 * console.log(scale(0));    // '#f7f7f7' (中央値の色、白)
 * console.log(scale(100));  // '#053061' (正の最大値の色、青)
 * ```
 */
export function createDivergingScale(
  paletteId: string,
  domain?: [number, number]
): d3.ScaleDiverging<string, never> {
  const palette = getPaletteById(paletteId);

  logger.assert(
    palette != null,
    `[PaletteUtils] Palette not found: ${paletteId}`
  );
  logger.assert(
    isDivergingPalette(palette!),
    `[PaletteUtils] Palette is not diverging: ${paletteId} (type: ${palette!.type})`
  );

  // D3の補間関数を作成
  const interpolator = d3.piecewise(d3.interpolateRgb.gamma(2.2), palette!.colors);
  const scale = d3.scaleDiverging(interpolator);

  if (domain) {
    scale.domain(domain);
  }

  return scale as d3.ScaleDiverging<string, never>;
}

/**
 * パレットから色配列を取得（シンプルなアクセサ）
 *
 * @param paletteId パレットID
 * @returns 色の配列
 */
export function getPaletteColors(paletteId: string): string[] {
  const palette = getPaletteById(paletteId);
  logger.assert(
    palette != null,
    `[PaletteUtils] Palette not found: ${paletteId}`
  );
  return palette!.colors;
}

/**
 * デフォルトパレットIDを取得
 */
export const DEFAULT_PALETTE_IDS = {
  categorical: 'midas-default',
  sequential: 'viridis',
  diverging: 'rdbu',
} as const;

/**
 * デフォルトカテゴリカルスケールを作成
 *
 * @param domain ドメイン（オプション）
 * @returns D3のカテゴリカルスケール
 */
export function createDefaultCategoricalScale(
  domain?: string[] | number[]
): d3.ScaleOrdinal<string | number, string, never> {
  return createCategoricalScale(DEFAULT_PALETTE_IDS.categorical, domain);
}

/**
 * デフォルトシーケンシャルスケールを作成
 *
 * @param domain ドメイン（オプション）
 * @returns D3のシーケンシャルスケール
 */
export function createDefaultSequentialScale(
  domain?: [number, number]
): d3.ScaleSequential<string, never> {
  return createSequentialScale(DEFAULT_PALETTE_IDS.sequential, domain);
}

/**
 * デフォルトダイバージングスケールを作成
 *
 * @param domain ドメイン（オプション）
 * @returns D3のダイバージングスケール
 */
export function createDefaultDivergingScale(
  domain?: [number, number]
): d3.ScaleDiverging<string, never> {
  return createDivergingScale(DEFAULT_PALETTE_IDS.diverging, domain);
}

/**
 * シーケンシャルパレットから補間関数を取得
 *
 * 密度可視化などで直接補間関数が必要な場合に使用。
 * 入力値 t (0〜1) に対して色を返す関数を生成する。
 *
 * @param paletteId パレットID
 * @returns 補間関数 (t: number) => string
 *
 * @example
 * ```typescript
 * const interpolator = getSequentialInterpolator('viridis');
 * console.log(interpolator(0));   // '#440154' (最小値の色)
 * console.log(interpolator(0.5)); // 中間の色
 * console.log(interpolator(1));   // '#fde724' (最大値の色)
 * ```
 */
export function getSequentialInterpolator(
  paletteId: string
): (t: number) => string {
  const palette = getPaletteById(paletteId);

  logger.assert(
    palette != null,
    `[PaletteUtils] Palette not found: ${paletteId}`
  );
  logger.assert(
    isSequentialPalette(palette!),
    `[PaletteUtils] Palette is not sequential: ${paletteId} (type: ${palette!.type})`
  );

  // D3の補間関数を作成して返す
  return d3.piecewise(d3.interpolateRgb.gamma(2.2), palette!.colors);
}
